import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import {
  FiCalendar,
  FiMapPin,
  FiBriefcase,
  FiAward,
  FiTarget,
  FiUsers,
} from "react-icons/fi";
import LoginPrompt from "./LoginPrompt";
import FullButton from "../Buttons/FullButton";
import {
  BannerHeader,
  Body,
  HeaderInfo,
  Section,
  StatusMessage,
} from "../../style/view.styles";
import {
  formatDate,
  IconContainer,
  MetadataContainer,
  MetadataContent,
  MetadataItem,
  MetadataLabel,
  MetadataValue,
  MotivationCard,
  MotivationItem,
  LevelCard,
  LevelItem,
  LevelTitle,
  LevelDescription,
  EligibilityList,
  EligibilityItem,
  CardHeader,
  PrizesList,
  PrizeItem,
  ExternalLink,
} from "../../style/opportunity.details.style";
import { fetchEntityData } from "../../utils/RequestHandler";
import LoadingSpinner from "./LoadingSpinner";

const ProgramDetailsBody = ({
  program,
  isLoggedIn,
  user,
  isApplyModalOpen,
  setIsApplyModalOpen,
  isApplicationSubmitted,
}) => {
  const [loadingRecognitions, setLoadingRecognitions] = useState(true);
  const [loadingMotivations, setLoadingMotivations] = useState(true);
  const [loadingCriterias, setLoadingCriterias] = useState(true);

  const [criterias, setCriterias] = useState([]);
  const [motivations, setMotivations] = useState([]);
  const [recognitions, setRecognitions] = useState([]);
  const [isJointProgram, setIsJointProgram] = useState(false);

  useEffect(() => {
    const fetchMotivations = async () => {
      try {
        const data = await fetchEntityData("program_motivations");
        if (data.success) {
          const records = data.result.filter(
            (record) => record.program === program.name
          );
          setMotivations(records);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingMotivations(false);
      }
    };

    const fetchRecognitions = async () => {
      try {
        const data = await fetchEntityData("program_recognitions");
        if (data.success) {
          const records = data.result.filter(
            (record) => record.program === program.name
          );
          const grouped = {};

          records.forEach((item) => {
            const { level, prize } = item;
            if (!grouped[level]) {
              grouped[level] = [];
            }
            grouped[level].push(prize);
          });

          const result = Object.entries(grouped).map(
            ([level, prizes], index) => ({
              id: index + 1,
              level,
              prizes,
            })
          );

          setRecognitions(result);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingRecognitions(false);
      }
    };

    const fetchCriterias = async () => {
      try {
        const data = await fetchEntityData("program_eligibility");
        if (data.success) {
          const records = data.result.filter(
            (record) => record.program === program.name
          );
          setCriterias(records);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingCriterias(false);
      }
    };

    const fetchProgramData = async () => {
      await fetchMotivations();
      await fetchRecognitions();
      await fetchCriterias();
    };

    fetchProgramData();
    setIsJointProgram(
      program.name === "Student Challenge Series & Youth to Business Forum"
    );
  }, [program?.name]);

  return (
    <Body>
      {isJointProgram && (
        <BannerHeader
          program={program}
          bannerPath={`${process.env.PUBLIC_URL}/banner-y2b.jpg`}
        />
      )}
      {!isJointProgram && (
        <HeaderInfo>
          <h1 className="font40 darkGreenColor extraBold">{program.name}</h1>
        </HeaderInfo>
      )}
      <MetadataContainer>
        <MetadataItem>
          <IconContainer>
            <FiBriefcase />
          </IconContainer>
          <MetadataContent>
            <MetadataLabel>Program Type</MetadataLabel>
            <MetadataValue>{program.type}</MetadataValue>
          </MetadataContent>
        </MetadataItem>
        <MetadataItem>
          <IconContainer>
            <FiMapPin />
          </IconContainer>
          <MetadataContent>
            <MetadataLabel>Delivery Mode</MetadataLabel>
            <MetadataValue>{program.delivery_mode}</MetadataValue>
          </MetadataContent>
        </MetadataItem>
        <MetadataItem>
          <IconContainer>
            <FiCalendar />
          </IconContainer>
          <MetadataContent>
            <MetadataLabel>Start Date</MetadataLabel>
            <MetadataValue>{formatDate(program.start_date)}</MetadataValue>
          </MetadataContent>
        </MetadataItem>
        <MetadataItem>
          <IconContainer>
            <FiCalendar />
          </IconContainer>
          <MetadataContent>
            <MetadataLabel>End Date</MetadataLabel>
            <MetadataValue>{formatDate(program.end_date)}</MetadataValue>
          </MetadataContent>
        </MetadataItem>
      </MetadataContainer>

      <Section>
        <h3 className="font24 extraBold">Description</h3>
        <div
          className="font18"
          style={{ padding: "30px 0" }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(program.description || ""),
          }}
        />
      </Section>
      {!isJointProgram && (
        <Section>
          {motivations?.length > 0 && (
            <h3 className="font24 extraBold">Why Join This Program</h3>
          )}
          <MotivationCard>
            {loadingMotivations && <LoadingSpinner />}
            {!loadingMotivations &&
              motivations.map((item) => (
                <MotivationItem key={item.id}>
                  <IconContainer>
                    <FiTarget />
                  </IconContainer>
                  <div>
                    <CardHeader>{item.motivation}</CardHeader>
                    <p>{item.description}</p>
                  </div>
                </MotivationItem>
              ))}
          </MotivationCard>
        </Section>
      )}
      {/* New Section: Program Recognitions & Prizes */}
      {!isJointProgram && (
        <Section>
          {recognitions?.length > 0 && (
            <>
              <h3 className="font24 extraBold">
                Program Recognitions & Prizes
              </h3>
              <p className="font18" style={{ paddingTop: "15px" }}>
                Participants can earn the following rewards based on performance
                level:
              </p>
            </>
          )}
          <LevelCard>
            {loadingRecognitions && <LoadingSpinner />}
            {!loadingRecognitions &&
              recognitions.map((item) => (
                <LevelItem key={item.id}>
                  <IconContainer>
                    <FiAward />
                  </IconContainer>
                  <div>
                    <LevelTitle>{item.level}</LevelTitle>
                    <LevelDescription>
                      <PrizesList>
                        {item.prizes.map((prize, index) => (
                          <PrizeItem key={index}>{prize}</PrizeItem>
                        ))}
                      </PrizesList>
                    </LevelDescription>
                  </div>
                </LevelItem>
              ))}
          </LevelCard>
        </Section>
      )}

      {/* New Section: Who Can Join */}
      {!isJointProgram && (
        <Section>
          {criterias?.length > 0 && (
            <h3 className="font24 extraBold">Who Can Join</h3>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              padding: "20px 0",
            }}
          >
            {criterias?.length > 0 && (
              <IconContainer style={{ marginTop: "5px" }}>
                <FiUsers />
              </IconContainer>
            )}

            <div>
              {criterias?.length > 0 && (
                <p className="font18" style={{ marginBottom: "20px" }}>
                  This program is designed for motivated individuals who meet
                  the following criteria:
                </p>
              )}
              <EligibilityList>
                {loadingCriterias && <LoadingSpinner />}
                {!loadingCriterias &&
                  criterias.map((item, index) => (
                    <EligibilityItem key={index}>
                      {item.criteria}
                    </EligibilityItem>
                  ))}
              </EligibilityList>
            </div>
          </div>
        </Section>
      )}
      {isJointProgram && (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <p className="font16">
            You can apply through{" "}
            <ExternalLink
              href="https://docs.google.com/forms/d/e/1FAIpQLSe_lMDcHbNMfldcdwyncG414WR2hpwwO90B7LFsxuesci04kw/viewform"
              target="_blank"
              rel="noopener noreferrer"
            >
              Youth to Business Form
            </ExternalLink>
          </p>
        </div>
      )}
      {!isJointProgram && (
        <Section>
          {!isLoggedIn && (
            <LoginPrompt
              message="Please log in to access the application form."
              buttonText="Go To Login"
            />
          )}
          {isLoggedIn && user.role === "talent" && !isApplicationSubmitted && (
            <FullButton
              action={() => {
                setIsApplyModalOpen(!isApplyModalOpen);
              }}
              title={"Join Program"}
            />
          )}
          {isApplicationSubmitted && (
            <StatusMessage type="success">
              Application submitted successfully.
            </StatusMessage>
          )}
        </Section>
      )}
    </Body>
  );
};

export default ProgramDetailsBody;
